/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PublicViewConfigPanel — renders all 8 accordion sections.
 */

import React, { useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import PageUrlSection from './sections/PageUrlSection';
import LayoutSection from './sections/LayoutSection';
import CustomCssSection from './sections/CustomCssSection';
import LayoutPageSection from './sections/LayoutPageSection';
import RelatedDataSection from './sections/RelatedDataSection';
import ActionButtonsSection from './sections/ActionButtonsSection';
import FieldsSection from './sections/FieldsSection';
import FieldMappingSection from './sections/FieldMappingSection';
import InteractionSection from './sections/InteractionSection';
import BrandingSection from './sections/BrandingSection';
import SeoSection from './sections/SeoSection';

const SECTIONS = [
  { id: 'url',         label: 'Page URL',           icon: 'bi-link-45deg' },
  { id: 'layout',      label: 'Layout',             icon: 'bi-layout-wtf' },
  { id: 'custom_css',  label: 'Custom CSS',         icon: 'bi-filetype-css' },
  { id: 'layout_pages', label: 'HTML Layout',       icon: 'bi-code-square', customOnly: true },
  // { id: 'related_data', label: 'Related Data',      icon: 'bi-diagram-3',   customOnly: true },
  { id: 'related_data', label: 'Related Data',      icon: 'bi-diagram-3' },
  { id: 'action_btns',  label: 'Action Buttons',    icon: 'bi-cursor',      customOnly: true },
  { id: 'mapping',     label: 'Field Mapping',      icon: 'bi-arrow-left-right' },
  { id: 'fields',      label: 'Fields',             icon: 'bi-list-columns' },
  { id: 'interaction', label: 'Visitor Interaction', icon: 'bi-hand-index' },
  { id: 'branding',    label: 'Branding',           icon: 'bi-palette' },
  { id: 'seo',         label: 'SEO',                icon: 'bi-search' },
];

function AccordionSection({ id, label, icon, danger, open, onToggle, children, theme }) {
  return (
    <div
      className="border rounded overflow-hidden mb-2"
      style={{ borderColor: danger && open ? '#fca5a5' : '#e2e8f0' }}
    >
      <button
        type="button"
        className="w-100 d-flex align-items-center gap-2 px-3 py-2 text-start"
        onClick={onToggle}
        style={{
          background: open
            ? (danger ? '#fef2f2' : '#f0f9ff')
            : '#fff',
          border: 'none',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
      >
        <i
          className={`bi ${icon}`}
          style={{ fontSize: 16, color: danger ? '#ef4444' : theme.primary }}
        />
        <span className="fw-semibold small flex-grow-1" style={{ color: danger ? '#ef4444' : '#1e293b' }}>
          {label}
        </span>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'} text-muted`} style={{ fontSize: 12 }} />
      </button>
      {open && (
        <div className="px-3 py-3" style={{ background: '#fafafa', borderTop: '1px solid #e2e8f0' }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function PublicViewConfigPanel({ tableId, config, updateConfig, fields, tableDisplayName, tableDescription, recordTemplatePages, tableName, relatedEdges, relatedTables, ownFields, writableTables, forms, loadChildEdges }) {
  const { theme } = useTheme();
  const [openSection, setOpenSection] = useState('layout');

  function toggle(id) {
    setOpenSection(prev => prev === id ? null : id);
  }

  return (
    <div>
      {SECTIONS.filter(sec => !sec.customOnly || config?.view_type === 'custom').map(sec => (
        <AccordionSection
          key={sec.id}
          {...sec}
          open={openSection === sec.id}
          onToggle={() => toggle(sec.id)}
          theme={theme}
        >
          {sec.id === 'url' && (
            <PageUrlSection tableId={tableId} config={config} updateConfig={updateConfig} theme={theme} />
          )}
          {sec.id === 'layout' && (
            <LayoutSection config={config} updateConfig={updateConfig} fields={fields} theme={theme} />
          )}
          {sec.id === 'layout_pages' && (
            <LayoutPageSection
              config={config}
              updateConfig={updateConfig}
              theme={theme}
              pages={recordTemplatePages}
              tableName={tableName}
            />
          )}
          {sec.id === 'related_data' && (
            <RelatedDataSection
              config={config}
              updateConfig={updateConfig}
              theme={theme}
              edges={relatedEdges}
              tables={relatedTables}
              ownFields={ownFields}
              tableName={tableName}
              loadChildEdges={loadChildEdges}
            />
          )}
          {sec.id === 'action_btns' && (
            <ActionButtonsSection
              config={config}
              updateConfig={updateConfig}
              theme={theme}
              tableName={tableName}
              writableTables={writableTables}
              forms={forms}
              recordColumns={fields}
              relatedEdges={relatedEdges}
            />
          )}
          {sec.id === 'custom_css' && (
            <CustomCssSection config={config} updateConfig={updateConfig} theme={theme} />
          )}
          {sec.id === 'fields' && (
            <FieldsSection config={config} updateConfig={updateConfig} fields={fields} theme={theme} />
          )}
          {sec.id === 'mapping' && (
            <FieldMappingSection config={config} updateConfig={updateConfig} fields={fields} theme={theme} />
          )}
          {sec.id === 'interaction' && (
            <InteractionSection
              config={config}
              updateConfig={updateConfig}
              fields={fields}
              theme={theme}
              relatedEdges={relatedEdges}
            />
          )}
          {sec.id === 'branding' && (
            <BrandingSection
              config={config}
              updateConfig={updateConfig}
              theme={theme}
              tableDisplayName={tableDisplayName}
              tableDescription={tableDescription}
            />
          )}
          {sec.id === 'seo' && (
            <SeoSection config={config} updateConfig={updateConfig} fields={fields} theme={theme} />
          )}
        </AccordionSection>
      ))}
    </div>
  );
}
